import { z } from "zod";
import valid from "card-validator";

/**
 * Payment step schema — credit card data + terms acceptance.
 *
 * Card values are validated client-side just enough to catch obvious typos
 * and reject unsupported brands early. Real validation happens at Omise
 * (Luhn re-check, BIN range, AVS).
 *
 * Security note:
 *  These fields hold raw PAN/CVV briefly while the user types and until
 *  Omise.createToken returns. They MUST never be sent to our backend.
 *  In useSubmitSubscription we tokenize first, then forward only the token.
 */

const currentYear = new Date().getFullYear();

// Brand whitelist — must match what Omise account has enabled.
// Amex is OFF by default on Omise; enable in Dashboard → Card Acceptance.
const ALLOWED_BRANDS = ["visa", "mastercard", "jcb", "american-express"];

const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  jcb: "JCB",
  "american-express": "American Express",
};

const ACCEPTED_LIST = ALLOWED_BRANDS.map((b) => BRAND_LABEL[b]).join(", ");

export const paymentSchema = z
  .object({
    card_number: z
      .string()
      .transform((v) => v.replace(/\s/g, "")) // strip spaces user typed
      .pipe(
        z
          .string()
          .min(13, "หมายเลขบัตรไม่ถูกต้อง")
          .max(19, "หมายเลขบัตรไม่ถูกต้อง")
          .regex(/^\d+$/, "หมายเลขบัตรต้องเป็นตัวเลขเท่านั้น")
          .superRefine((val, ctx) => {
            const result = valid.number(val);

            // Luhn + format check (catches typos that pass length/digit regex)
            if (!result.isValid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "หมายเลขบัตรไม่ถูกต้อง",
              });
              return;
            }

            // Brand whitelist
            if (!ALLOWED_BRANDS.includes(result.card?.type)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `รองรับเฉพาะ ${ACCEPTED_LIST}`,
              });
            }
          })
      ),

    card_holder: z
      .string()
      .trim()
      .min(1, "กรุณากรอกชื่อบนบัตร")
      .max(100, "ชื่อบนบัตรยาวเกินไป")
      // Most issuers print Latin letters only; reject Thai chars to avoid
      // "name mismatch" decline at the gateway.
      .regex(/^[A-Za-z\s.\-']+$/, "ใช้ตัวอักษรภาษาอังกฤษเท่านั้น"),

    expiry_month: z
      .union([z.string(), z.number()])
      .transform((v) => Number(v))
      .pipe(
        z
          .number()
          .int()
          .min(1, "เดือนไม่ถูกต้อง")
          .max(12, "เดือนไม่ถูกต้อง")
      ),

    expiry_year: z
      .union([z.string(), z.number()])
      .transform((v) => {
        const n = Number(v);
        // Accept 2-digit (24) or 4-digit (2024) input
        return n < 100 ? 2000 + n : n;
      })
      .pipe(
        z
          .number()
          .int()
          .min(currentYear, "บัตรหมดอายุ")
          .max(currentYear + 20, "ปีไม่ถูกต้อง")
      ),

    cvv: z.string().regex(/^\d{3,4}$/, "CVV ต้องเป็นตัวเลข 3-4 หลัก"),

    accept_terms: z.boolean().refine((v) => v === true, {
      message: "กรุณายอมรับเงื่อนไขก่อนชำระเงิน",
    }),
  })
  // Cross-field check: if year === currentYear, month must be in the future.
  .refine(
    (data) => {
      if (data.expiry_year !== currentYear) return true;
      const currentMonth = new Date().getMonth() + 1;
      return data.expiry_month >= currentMonth;
    },
    {
      message: "บัตรหมดอายุ",
      path: ["expiry_month"],
    }
  )
  // Cross-field check: Amex requires 4-digit CVV; others require 3.
  // Only runs when card_number itself is valid (otherwise brand is unknown).
  .superRefine((data, ctx) => {
    const brand = valid.number(data.card_number).card?.type;
    if (!brand) return; // card_number invalid; let its own error speak first

    const expectedLen = brand === "american-express" ? 4 : 3;
    if (data.cvv.length !== expectedLen) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cvv"],
        message: `CVV ต้องมี ${expectedLen} หลัก`,
      });
    }
  });